import debounce from 'lodash.debounce';
import {
  FocusEventHandler,
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import Autosuggest, {
  ChangeEvent,
  RenderInputComponentProps,
  RenderSuggestionsContainerParams,
} from 'react-autosuggest';

import {
  InputPrefixVariant,
  InputStyleVariant,
} from 'components/Input/Input.styled';
import { VisuallyHidden } from 'components/VisuallyHidden';
import { REMOTE_KEYWORDS } from 'utils/constants/search';

import { AutosuggestWrapper } from './AutosuggestWrapper';
import { LocationInputSuggestion } from './LocationInputSuggestion';
import { StyledInput } from './StyledInput';
import { SuggestionsContainer } from './SuggestionsContainer';
import { getCity } from './getCity';
import { getPlacePredictions } from './getPlacePredictions';
import { getRemoteOptions } from './getRemoteOptions';
import { useLoadGoogleMapsAutocomplete } from './useLoadGoogleMapsAutocomplete';

const defaultKeyword = REMOTE_KEYWORDS[CURRENT_LOCALE][0];

type Props = {
  onLocationChange: (
    arg0: SearchLocation,
    arg1: boolean | null | undefined,
    arg2: boolean | null | undefined,
  ) => void;
  onLocationInputValueChange?: (
    event: FormEvent<unknown>,
    params: ChangeEvent,
  ) => void;
  hasSuccess?: boolean;
  clearLocation: () => void;
  locationInputValue: string;
  'data-qa-id'?: string;
  placeholder?: string;
  showLocationIcon?: boolean;
  showClearButton: boolean;
  className?: string;
  handlePlaceDetails?: (place: google.maps.GeocoderResult) => void;
  addressTypes?: Array<string>;
  includeCity?: boolean;
  includeFull?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  disabled?: boolean;
  hasRemoteOptions?: boolean;
  highlightFirstSuggestion?: boolean;
  componentRestrictions?: google.maps.places.ComponentRestrictions;
  prefixVariant?: InputPrefixVariant;
  styleVariant?: InputStyleVariant;
};

export function LocationInput({
  hasSuccess,
  onLocationChange,
  onLocationInputValueChange,
  clearLocation,
  locationInputValue,
  placeholder,
  showLocationIcon = false,
  showClearButton,
  className,
  addressTypes,
  includeCity,
  includeFull,
  disabled,
  highlightFirstSuggestion = true,
  componentRestrictions,
  hasRemoteOptions,
  onBlur,
  handlePlaceDetails: handlePlaceDetailsProp,
  'data-qa-id': dataQaId,
  prefixVariant,
  styleVariant,
}: Props) {
  const { autocompleteService, sessionToken, gMapsErrored, geocoder } =
    useLoadGoogleMapsAutocomplete();

  const handlePlaceDetails = useCallback(
    (place: google.maps.GeocoderResult) => {
      if (handlePlaceDetailsProp) {
        handlePlaceDetailsProp(place);
        return;
      }

      const addressComponents: Record<string, boolean | string> =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        place.address_components.reduce((hash: any, component: any) => {
          const attributeName = component.types[0];

          return {
            ...hash,
            [attributeName]: [
              'country',
              'administrative_area_level_1',
            ].includes(attributeName)
              ? component.short_name
              : component.long_name,
          };
        }, {});

      if (!('country' in addressComponents)) {
        // below: special handling of 'south east asia', 'gaza', etc.
        // i.e. results for which the autocomplete api does not return a country code
        // we will use the iso 3166 user-defined 'XA' code for this situation
        // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#User-assigned_code_elements
        addressComponents.country = 'XA';
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-param-reassign
        place.types[0] = 'undefined_country_geocodes';
      }

      const countryCode = addressComponents.country;
      const stateCode = addressComponents.administrative_area_level_1;
      let city;
      let full;

      if (includeCity) {
        city = getCity(addressComponents);
      }

      if (includeFull) {
        full = place.formatted_address;
      }

      const boundingBox = place.geometry.viewport.toJSON();
      // @ts-expect-error TS(2554): Expected 3 arguments, but got 1.
      onLocationChange({
        text: place.formatted_address,
        stateCode,
        countryCode,
        boundingBoxN: boundingBox.north,
        boundingBoxW: boundingBox.west,
        boundingBoxS: boundingBox.south,
        boundingBoxE: boundingBox.east,
        geoType: place.types[0],
        latLongAddress: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        full,
        city,
      });
    },
    [handlePlaceDetailsProp, includeCity, includeFull, onLocationChange],
  );

  const onSuggestionSelected = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (e: any, { suggestion }: any) => {
      if (!geocoder) return;

      if (suggestion.isRemoteOption) {
        onLocationChange(
          {
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
            text: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
            stateCode: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
            countryCode: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'number | un... Remove this comment to see the full error message
            boundingBoxN: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'number | un... Remove this comment to see the full error message
            boundingBoxW: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'number | un... Remove this comment to see the full error message
            boundingBoxS: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'number | un... Remove this comment to see the full error message
            boundingBoxE: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
            geoType: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'number | un... Remove this comment to see the full error message
            latitude: null,
            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'number | un... Remove this comment to see the full error message
            longitude: null,
            full: null,
            city: null,
          },
          true,
          true,
        );

        return;
      }

      await geocoder.geocode(
        {
          placeId: suggestion.place_id,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (results: any) => handlePlaceDetails(results[0]),
      );
    },
    [geocoder, handlePlaceDetails, onLocationChange],
  );

  const [suggestions, setSuggestions] = useState<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ReadonlyArray<Record<string, any>>
  >([]);

  const [inProgressQuery, setInProgressQuery] = useState<
    | Promise<
        ReadonlyArray<{
          name: string;
          value: string;
        }>
      >
    | null
    | undefined
  >(null);

  const loadSuggestions = useCallback(
    (value: string) => {
      // Cancel the previous request
      if (inProgressQuery) {
        inProgressQuery.then(() => {
          debounce(() => loadSuggestions(value));
        });
        return undefined;
      }

      if (!autocompleteService || !sessionToken) return;

      const query = getPlacePredictions({
        autocompleteService,
        input: value,
        componentRestrictions,
        addressTypes,
        sessionToken,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const remoteOptions: any = [];

      if (hasRemoteOptions) {
        remoteOptions.push(getRemoteOptions({ value, defaultKeyword }));
      }

      query.then((options) => {
        // @ts-expect-error TS(2488): Type 'unknown' must have a '[Symbol.iterator]()' m... Remove this comment to see the full error message
        setSuggestions([...remoteOptions, ...options].filter(Boolean));
        setInProgressQuery(null);
      });

      // @ts-expect-error TS(2345): Argument of type 'Promise<unknown>' is not assigna... Remove this comment to see the full error message
      setInProgressQuery(query);
      return query;
    },
    [
      addressTypes,
      autocompleteService,
      componentRestrictions,
      hasRemoteOptions,
      inProgressQuery,
      sessionToken,
    ],
  );

  const onSuggestionsFetchRequested = useCallback(
    ({ value }: { value: string }) => {
      if (autocompleteService) {
        loadSuggestions(value);
      }
    },
    [autocompleteService, loadSuggestions],
  );

  const renderSuggestion = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (suggestion: any) => <LocationInputSuggestion suggestion={suggestion} />,
    [],
  );

  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
    setInProgressQuery(null);
  }, []);

  const renderSuggestionsContainer = useCallback(
    ({ containerProps, children }: RenderSuggestionsContainerParams) => (
      <SuggestionsContainer containerProps={containerProps}>
        {children}
      </SuggestionsContainer>
    ),
    [],
  );

  const renderInputComponent = useCallback(
    ({
      autoComplete,
      className: inputClassName,
      disabled: inputDisabled,
      id,
      onBlur: inputOnBlur,
      onChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      placeholder: inputPlaceholder,
      ref,
      title,
      type,
      value,
      ...renderInputComponentProps
    }: RenderInputComponentProps) =>
      (
        <>
          <VisuallyHidden>
            <label htmlFor="location-input">{getText('Location')}</label>
          </VisuallyHidden>
          <StyledInput
            aria-activedescendant={
              renderInputComponentProps['aria-activedescendant']
            }
            ref={ref}
            aria-autocomplete={renderInputComponentProps['aria-autocomplete']}
            aria-controls={renderInputComponentProps['aria-controls']}
            autoComplete={autoComplete}
            className={inputClassName}
            disabled={inputDisabled}
            id={id}
            onBlur={inputOnBlur}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            hasSuccess={hasSuccess}
            onKeyPress={onKeyPress}
            placeholder={placeholder}
            title={title}
            type={type}
            value={value}
            onClear={showClearButton ? clearLocation : null}
            gMapsErrored={gMapsErrored}
            showLocationIcon={showLocationIcon}
            styleVariant={styleVariant}
            prefixVariant={prefixVariant}
            data-qa-id={dataQaId || 'location-input'}
          />
        </>
      ) as ReactNode,
    [
      clearLocation,
      gMapsErrored,
      hasSuccess,
      placeholder,
      dataQaId,
      prefixVariant,
      styleVariant,
      showClearButton,
      showLocationIcon,
    ],
  );

  const onKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }, []);

  const getSuggestionValue = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (suggestion: any) => suggestion.description,
    [],
  );

  return (
    <AutosuggestWrapper borderSize={4}>
      {/* @ts-expect-error TS(2769): No overload matches this call. */}
      <Autosuggest
        highlightFirstSuggestion={highlightFirstSuggestion}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={(v) => v && v.toString().trim().length >= 2}
        renderInputComponent={renderInputComponent}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{
          id: 'location-input',
          className,
          disabled,
          onChange: onLocationInputValueChange,
          onBlur,
          onKeyPress,
          placeholder,
          value: locationInputValue || '',
          title: getText('Location'),
        }}
      />
    </AutosuggestWrapper>
  );
}
